import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties
} from "react";
import { flushSync } from "react-dom";
import { Button } from "../components/Button";
import { CardFaceContent, CardTile } from "../components/CardTile";
import {
  CardsIcon,
  DrawCardIcon,
  EyeIcon,
  EyeOffIcon,
  FilmIcon,
  HistoryIcon,
  MegaphoneIcon
} from "../components/Icons";
import { ScreenShell } from "../components/ScreenShell";
import { useUiText } from "../i18n/useUiText";
import type { SessionCard } from "../session/types";
import { useAppStore } from "../state/useAppStore";
import { playAccuseSound } from "../util/accuseSound";

interface CardRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

interface InspectedDiscard {
  readonly cardId: string;
  readonly origin: CardRect;
  readonly phase: "open" | "returning" | "handoff";
}

type HandScene =
  | { readonly kind: "idle" }
  | {
      readonly kind: "reveal";
      readonly cardId: string;
      readonly origin: CardRect;
      readonly phase: "open" | "returning" | "handoff" | "discarding";
      readonly discardTarget?: CardRect;
    }
  | {
      readonly kind: "discard";
      readonly inspected: InspectedDiscard | null;
      readonly phase: "open" | "closing" | "undoing";
      readonly flightOrigin?: CardRect;
    }
  | {
      readonly kind: "draw";
      readonly phase: "ready" | "flipping" | "closing";
      readonly flightOrigin?: CardRect;
    };

const CARD_MOVE_MS = 380;
const DISCARD_AFTER_RETURN_MS = 320;
const DRAW_FLIGHT_MS = 460;
const UNDO_FLIGHT_MS = 340;
const SCENE_CLOSE_MS = 360;
const CARD_HANDOFF_MS = 32;

function rectFrom(element: Element): CardRect {
  const rect = element.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

function StageCard({ card, className = "" }: { readonly card: SessionCard; readonly className?: string }) {
  const dual = card.cardKind === "DUAL";

  return (
    <article
      aria-label={card.displayText}
      className={`stage-card card-face${dual ? " stage-card--dual" : ""} ${className}`.trim()}
    >
      <CardFaceContent card={card} />
    </article>
  );
}

function CardBack({ layer = 0 }: { readonly layer?: number }) {
  return (
    <div className="deck-card" style={{ "--deck-layer": layer } as CSSProperties}>
      <span>You Said It!</span>
    </div>
  );
}

function RevealStage({
  card,
  discardTarget,
  origin,
  phase,
  nested = false,
  onReturn
}: {
  readonly card: SessionCard;
  readonly origin: CardRect;
  readonly phase: "open" | "returning" | "handoff" | "discarding";
  readonly discardTarget?: CardRect;
  readonly nested?: boolean;
  readonly onReturn: () => void;
}) {
  const floatingRef = useRef<HTMLDivElement>(null);
  const crispRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [targetRect, setTargetRect] = useState<CardRect | null>(null);

  useLayoutEffect(() => {
    const element = floatingRef.current;
    if (element === null) return;
    const sceneRect = element.closest(".card-scene")?.getBoundingClientRect();
    if (sceneRect === undefined) return;
    const targetWidth = Math.min(sceneRect.width * 0.76, 400);
    const targetHeight = targetWidth * 7 / 5;
    const targetLeft = sceneRect.left + (sceneRect.width - targetWidth) / 2;
    const targetTop = sceneRect.top + (sceneRect.height - targetHeight) / 2;
    setTargetRect({ top: targetTop, left: targetLeft, width: targetWidth, height: targetHeight });
    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => setReady(true));
    });
    return () => {
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [origin]);

  useLayoutEffect(() => {
    const element = crispRef.current;
    if (element === null || discardTarget === undefined || targetRect === null) return;
    const scale = 0.12;
    const targetCenterX = discardTarget.left + discardTarget.width / 2;
    const targetCenterY = discardTarget.top + discardTarget.height / 2;
    element.style.setProperty(
      "--card-discard-x",
      `${targetCenterX - targetRect.left - targetRect.width * scale / 2}px`
    );
    element.style.setProperty(
      "--card-discard-y",
      `${targetCenterY - targetRect.top - targetRect.height * scale / 2}px`
    );
  }, [discardTarget, targetRect]);

  // The crisp card owns the grow + rest and is laid out at its final size, so its
  // text is rasterised natively and never shifts as it settles. The native-small
  // floating clone owns only the shrink so it lands pixel-identical on the grid
  // tile. Neither element re-lays-out at a different size mid-animation.
  const floatingStyle: CSSProperties = targetRect === null
    ? { top: origin.top, left: origin.left, width: origin.width, height: origin.height }
    : {
        top: origin.top,
        left: origin.left,
        width: origin.width,
        height: origin.height,
        "--card-open-x": `${targetRect.left - origin.left}px`,
        "--card-open-y": `${targetRect.top - origin.top}px`,
        "--card-open-scale": `${targetRect.width / origin.width}`
      } as CSSProperties;

  return (
    <>
      {nested ? (
        <div
          className={`nested-card-scrim${ready ? " nested-card-scrim--ready" : ""}`}
          data-phase={phase}
          aria-hidden="true"
        />
      ) : null}
      <div
        className={`floating-reveal-card${ready ? " floating-reveal-card--ready" : ""}${nested ? " floating-reveal-card--nested" : ""}`}
        data-phase={phase}
        lang="zh-CN"
        ref={floatingRef}
        style={floatingStyle}
      >
        <CardTile card={card} onSelect={onReturn} selected={false} />
      </div>
      {targetRect === null ? null : (
        <div
          className={`crisp-reveal-card${ready ? " crisp-reveal-card--ready" : ""}`}
          data-phase={phase}
          lang="zh-CN"
          onClick={phase === "open" ? (event) => { event.stopPropagation(); onReturn(); } : undefined}
          ref={crispRef}
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            "--card-origin-x": `${origin.left - targetRect.left}px`,
            "--card-origin-y": `${origin.top - targetRect.top}px`,
            "--card-origin-scale": `${origin.width / targetRect.width}`
          } as CSSProperties}
        >
          <StageCard card={card} />
        </div>
      )}
    </>
  );
}

function FlyingCard({
  card,
  origin,
  flip
}: {
  readonly card: SessionCard;
  readonly origin: CardRect;
  readonly flip: boolean;
}) {
  const [flipped, setFlipped] = useState(!flip);
  const [landed, setLanded] = useState(false);
  const [box, setBox] = useState(origin);

  useLayoutEffect(() => {
    const flipFrame = window.requestAnimationFrame(() => setFlipped(true));
    const landingTimer = window.setTimeout(() => {
      const target = document.querySelector("[data-hand-incoming]");
      if (target !== null) setBox(rectFrom(target));
      setLanded(true);
    }, flip ? 180 : 30);
    return () => {
      window.cancelAnimationFrame(flipFrame);
      window.clearTimeout(landingTimer);
    };
  }, [flip]);

  const style: CSSProperties = {
    top: box.top,
    left: box.left,
    width: box.width,
    height: box.height
  };

  return (
    <div
      className={`card-flight${landed ? " card-flight--landed" : ""}`}
      style={style}
    >
      <div className={`card-flight__inner${flipped ? " card-flight__inner--flipped" : ""}`}>
        <div className="card-flight__face card-flight__back"><CardBack /></div>
        <div className="card-flight__face card-flight__front"><StageCard card={card} /></div>
      </div>
    </div>
  );
}

export function HandScreen() {
  const t = useUiText();
  const [scene, setScene] = useState<HandScene>({ kind: "idle" });
  const [accusing, setAccusing] = useState(false);
  const session = useAppStore((state) => state.session);
  const selectedSeat = useAppStore((state) => state.selectedSeat);
  const hand = useAppStore((state) => state.hand);
  const penaltyQueue = useAppStore((state) => state.penaltyQueue);
  const history = useAppStore((state) => state.history);
  const handCovered = useAppStore((state) => state.handCovered);
  const handIntroPending = useAppStore((state) => state.handIntroPending);
  const goHome = useAppStore((state) => state.goHome);
  const toggleCover = useAppStore((state) => state.toggleCover);
  const revealHandIntro = useAppStore((state) => state.revealHandIntro);
  const openThemeReview = useAppStore((state) => state.openThemeReview);
  const discardCard = useAppStore((state) => state.discardCard);
  const drawPenalty = useAppStore((state) => state.drawPenalty);
  const undoLastDiscard = useAppStore((state) => state.undoLastDiscard);

  useEffect(() => {
    if (!handIntroPending) return;
    const timer = window.setTimeout(revealHandIntro, 500);
    return () => window.clearTimeout(timer);
  }, [handIntroPending, revealHandIntro]);

  useEffect(() => {
    if (scene.kind === "idle") return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setScene({ kind: "idle" });
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [scene.kind]);

  const revealedCard = scene.kind === "reveal"
    ? hand.find((card) => card.cardId === scene.cardId)
    : undefined;
  const inspectedDiscard = scene.kind === "discard" && scene.inspected !== null
    ? history.find((entry) => entry.card.cardId === scene.inspected?.cardId)?.card
    : undefined;
  const lastDiscard = history.at(-1)?.card;
  const nextPenalty = penaltyQueue[0];
  const incomingCard = scene.kind === "draw" && scene.flightOrigin !== undefined && (scene.phase === "flipping" || scene.phase === "closing")
    ? nextPenalty
    : scene.kind === "discard" && scene.phase === "undoing"
      ? lastDiscard
      : undefined;
  if (session === null || selectedSeat === null) return null;

  const closeScene = () => {
    if (scene.kind === "discard") {
      setScene({ ...scene, phase: "closing", inspected: null });
    } else if (scene.kind === "draw") {
      setScene({ kind: "draw", phase: "closing" });
    } else if (scene.kind === "reveal") {
      setScene({ ...scene, phase: "returning" });
      window.setTimeout(() => {
        setScene((current) => current.kind === "reveal" ? { ...current, phase: "handoff" } : current);
      }, CARD_MOVE_MS);
      window.setTimeout(() => setScene({ kind: "idle" }), CARD_MOVE_MS + CARD_HANDOFF_MS);
      return;
    } else return;
    window.setTimeout(() => setScene({ kind: "idle" }), SCENE_CLOSE_MS);
  };

  const discardRevealed = () => {
    if (scene.kind !== "reveal") return;
    const target = document.querySelector(".screen-footer button:last-child");
    setScene({
      ...scene,
      phase: "discarding",
      ...(target === null ? {} : { discardTarget: rectFrom(target) })
    });
    window.setTimeout(() => {
      const positions = new Map(
        [...document.querySelectorAll<HTMLElement>(".card-grid > [data-card-id]")]
          .filter((element) => element.dataset.cardId !== scene.cardId)
          .map((element) => [element.dataset.cardId!, rectFrom(element)] as const)
      );
      flushSync(() => {
        discardCard(scene.cardId);
        setScene({ kind: "idle" });
      });
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      window.requestAnimationFrame(() => {
        for (const element of document.querySelectorAll<HTMLElement>(".card-grid > [data-card-id]")) {
          const before = positions.get(element.dataset.cardId ?? "");
          if (before === undefined) continue;
          const after = element.getBoundingClientRect();
          const x = before.left - after.left;
          const y = before.top - after.top;
          if (Math.abs(x) < 0.5 && Math.abs(y) < 0.5) continue;
          element.animate(
            [{ transform: `translate(${x}px, ${y}px)` }, { transform: "none" }],
            { duration: 240, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
          );
        }
      });
    }, DISCARD_AFTER_RETURN_MS);
  };

  const returnInspectedDiscard = () => {
    if (scene.kind !== "discard" || scene.inspected === null) return;
    setScene({ ...scene, inspected: { ...scene.inspected, phase: "returning" } });
    window.setTimeout(() => {
      setScene((current) => current.kind === "discard" && current.inspected !== null
        ? { ...current, inspected: { ...current.inspected, phase: "handoff" } }
        : current);
    }, CARD_MOVE_MS);
    window.setTimeout(() => {
      setScene((current) => current.kind === "discard" ? { ...current, inspected: null } : current);
    }, CARD_MOVE_MS + CARD_HANDOFF_MS);
  };

  const undoDiscard = () => {
    if (scene.kind !== "discard" || lastDiscard === undefined) return;
    const tile = document.querySelector(`[data-card-id="${CSS.escape(lastDiscard.cardId)}"]`);
    const drawer = document.querySelector(".discard-stage");
    const origin = tile !== null
      ? rectFrom(tile)
      : drawer !== null
        ? rectFrom(drawer)
        : { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 1, height: 1 };
    setScene({ ...scene, inspected: null, phase: "undoing", flightOrigin: origin });
    window.setTimeout(() => {
      undoLastDiscard();
      setScene({ kind: "idle" });
    }, UNDO_FLIGHT_MS);
  };

  const draw = () => {
    if (scene.kind !== "draw" || nextPenalty === undefined) return;
    const deck = document.querySelector(".deal-deck");
    if (deck === null) return;
    setScene({ kind: "draw", phase: "flipping", flightOrigin: rectFrom(deck) });
    window.setTimeout(() => {
      setScene((current) => current.kind === "draw" && current.flightOrigin !== undefined
        ? { ...current, phase: "closing" }
        : current);
    }, 300);
    window.setTimeout(() => {
      drawPenalty();
      setScene({ kind: "idle" });
    }, DRAW_FLIGHT_MS);
  };

  const mainFooter = (disabled: boolean) => (
    <div className="hand-action-bar">
      <Button className="hand-action" disabled={disabled} icon={<DrawCardIcon />} onClick={() => setScene({ kind: "draw", phase: "ready" })} variant="secondary" wide>{t("hand.drawWithCount", { count: penaltyQueue.length })}</Button>
      <Button className="hand-action" data-hand-action="discard" disabled={disabled} icon={<HistoryIcon />} onClick={() => setScene({ kind: "discard", inspected: null, phase: "open" })} variant="secondary" wide>{t("hand.historyWithCount", { count: history.length })}</Button>
    </div>
  );

  const footer = (() => {
    if (scene.kind === "reveal") {
      if (scene.phase !== "open") return mainFooter(true);
      return (
        <div className="hand-scene-actions">
          <Button onClick={discardRevealed} variant="danger" wide>{t("reveal.discard")}</Button>
          <Button onClick={closeScene} variant="secondary" wide>{t("reveal.putBack")}</Button>
        </div>
      );
    }
    if (scene.kind === "discard") {
      if (inspectedDiscard !== undefined) {
        return (
          <div className="hand-scene-actions">
            <Button disabled variant="secondary" wide>{t("history.undo")}</Button>
            <Button disabled={scene.inspected?.phase !== "open"} onClick={returnInspectedDiscard} variant="secondary" wide>{t("common.cancel")}</Button>
          </div>
        );
      }
      if (scene.phase !== "open") return mainFooter(true);
      return (
        <div className="hand-scene-actions">
          <Button disabled={history.length === 0} onClick={undoDiscard} variant="secondary" wide>{t("history.undo")}</Button>
          <Button onClick={closeScene} variant="secondary" wide>{t("common.cancel")}</Button>
        </div>
      );
    }
    if (scene.kind === "draw") {
      if (scene.phase !== "ready") return mainFooter(true);
      return (
        <div className="hand-scene-actions">
          <Button disabled={nextPenalty === undefined} onClick={draw} wide>{nextPenalty === undefined ? t("modal.noPenaltyTitle") : t("modal.drawConfirm")}</Button>
          <Button onClick={closeScene} variant="secondary" wide>{t("common.cancel")}</Button>
        </div>
      );
    }
    return mainFooter(false);
  })();

  return (
    <ScreenShell backLabel={t("common.home")} className="hand-screen" footer={footer} onBack={goHome}>
      <div className="hand-layout">
        <div className="hand-top">
          <Button className="hand-top__button" icon={<FilmIcon />} onClick={openThemeReview} variant="ghost">{t("hand.viewTheme")}</Button>
          <Button aria-label={handCovered ? t("hand.uncover") : t("hand.cover")} className="hand-cover-toggle" icon={handCovered ? <EyeIcon /> : <EyeOffIcon />} onClick={toggleCover} variant="ghost">{handCovered ? t("hand.uncover") : t("hand.cover")}</Button>
          <button className={`accuse-button${accusing ? " accuse-button--active" : ""}`} onClick={() => { playAccuseSound(); setAccusing(true); window.setTimeout(() => setAccusing(false), 500); }} type="button">
            <MegaphoneIcon /><span>{t("hand.accuse")}</span>
          </button>
        </div>

        <section className="hand-cards">
          {hand.length === 0 && incomingCard === undefined ? (
            <div className="empty-hand"><CardsIcon /><h2>{t("hand.emptyTitle")}</h2></div>
          ) : (
            <div aria-label={t("hand.cards")} className="card-grid" lang={session.theme.contentLocale}>
              {hand.map((card) => (
                <CardTile
                  card={card}
                  covered={handCovered}
                  ghosted={scene.kind === "reveal" && scene.cardId === card.cardId && scene.phase !== "handoff"}
                  key={card.cardId}
                  onSelect={(element) => {
                    if (!handCovered && scene.kind === "idle") {
                      setScene({ kind: "reveal", cardId: card.cardId, origin: rectFrom(element), phase: "open" });
                    }
                  }}
                  selected={false}
                />
              ))}
              {incomingCard === undefined ? null : <div className="card-tile card-slot-placeholder" data-hand-incoming />}
            </div>
          )}
        </section>
      </div>

      {scene.kind === "idle" ? null : (
        <div
          aria-label={scene.kind === "reveal" ? t("reveal.title") : scene.kind === "discard" ? t("history.title") : t("hand.draw")}
          aria-modal="true"
          className={`card-scene card-scene--${scene.kind} card-scene--${scene.phase}`}
          onClick={() => {
            if (scene.kind === "reveal" && scene.phase === "open") closeScene();
            if (scene.kind === "discard" && scene.inspected?.phase === "open") {
              returnInspectedDiscard();
            } else if (scene.kind === "discard" && scene.phase === "open") {
              closeScene();
            }
            if (scene.kind === "draw" && scene.phase === "ready") closeScene();
          }}
          role="dialog"
        >
          <div className="card-scene__scrim" aria-hidden="true" />

          {scene.kind === "reveal" && revealedCard !== undefined ? (
            <RevealStage
              card={revealedCard}
              {...(scene.discardTarget === undefined ? {} : { discardTarget: scene.discardTarget })}
              onReturn={closeScene}
              origin={scene.origin}
              phase={scene.phase}
            />
          ) : null}

          {scene.kind === "discard" ? (
            <section className={`discard-stage${scene.inspected === null ? "" : " discard-stage--inspecting"}`} aria-label={t("history.title")}>
              <p className="discard-stage__title" onClick={(event) => event.stopPropagation()}>{t("hand.history")}</p>
              {history.length === 0 ? <div className="empty-history"><HistoryIcon /><p>{t("history.empty")}</p></div> : (
                <div className="discard-grid" lang={session.theme.contentLocale}>
                  {history.map((entry) => (
                    <CardTile card={entry.card} ghosted={scene.inspected?.cardId === entry.card.cardId && scene.inspected.phase !== "handoff"} key={entry.historyId} onSelect={(element) => setScene({ ...scene, inspected: { cardId: entry.card.cardId, origin: rectFrom(element), phase: "open" } })} selected={false} />
                  ))}
                </div>
              )}
              <span className="discard-stage__count" onClick={(event) => event.stopPropagation()}>{t("history.count", { count: history.length })}</span>
            </section>
          ) : null}

          {scene.kind === "discard" && scene.inspected !== null && inspectedDiscard !== undefined ? <RevealStage card={inspectedDiscard} nested onReturn={returnInspectedDiscard} origin={scene.inspected.origin} phase={scene.inspected.phase} /> : null}
          {scene.kind === "discard" && scene.phase === "undoing" && lastDiscard !== undefined && scene.flightOrigin !== undefined ? <FlyingCard card={lastDiscard} flip={false} origin={scene.flightOrigin} /> : null}

          {scene.kind === "draw" ? (
            <div className="draw-stage" lang={session.theme.contentLocale}>
              <p>{nextPenalty === undefined ? t("modal.noPenaltyBody") : t("draw.prompt")}</p>
              {nextPenalty === undefined ? <div className="empty-deck"><CardsIcon /></div> : (
                <button
                  aria-label={t("modal.drawConfirm")}
                  className={`deal-deck deal-deck--${scene.phase}`}
                  disabled={scene.phase !== "ready"}
                  onClick={(event) => {
                    event.stopPropagation();
                    draw();
                  }}
                  type="button"
                >
                  {penaltyQueue.slice(0, 5).map((card, index) => <CardBack key={card.cardId} layer={Math.min(penaltyQueue.length, 5) - index - 1} />)}
                </button>
              )}
              <span className="draw-stage__count">{t("draw.remaining", { count: penaltyQueue.length })}</span>
            </div>
          ) : null}
          {scene.kind === "draw" && (scene.phase === "flipping" || scene.phase === "closing") && nextPenalty !== undefined && scene.flightOrigin !== undefined ? <FlyingCard card={nextPenalty} flip origin={scene.flightOrigin} /> : null}
        </div>
      )}
    </ScreenShell>
  );
}
